<script lang="ts">
  import { subscriptionType, openPricingModal } from '$lib/stores/subscription';
  import { SubscriptionService } from '$lib/services/subscription/SubscriptionService';
  import type { LicenseInfo } from '$lib/types/subscription';
  import { onMount } from 'svelte';
  
  // Import refactored components
  import SubscriptionStatusCard from '../subscription/SubscriptionStatusCard.svelte';
  import UsageStatistics from '../subscription/UsageStatistics.svelte';
  import FeatureComparisonTable from '../subscription/FeatureComparisonTable.svelte';
  import ActiveLicenseDisplay from '../subscription/ActiveLicenseDisplay.svelte';
  import LicenseActivationForm from '../subscription/LicenseActivationForm.svelte';
  import UsageResetTimer from '../subscription/UsageResetTimer.svelte';
  
  // License info state
  let currentLicenseInfo: LicenseInfo | null = null;
  
  // Load license info when component mounts or subscription changes
  $: if ($subscriptionType === 'premium' || $subscriptionType === 'beta') {
    loadLicenseInfo();
  }
  
  async function loadLicenseInfo() {
    const service = SubscriptionService.getInstance();
    // Force refresh to get latest data from server
    await service.updateSubscriptionStatus();
    currentLicenseInfo = await service.getCurrentLicenseInfo();
    
    // If maxDevices is still wrong, fix it locally
    if (currentLicenseInfo && currentLicenseInfo.maxDevices !== 5) {
      currentLicenseInfo.maxDevices = 5;
    }
  }
  
  function handleLicenseActivated() {
    loadLicenseInfo();
  }
  
  function handleLicenseDeactivated() {
    currentLicenseInfo = null;
  }
  
  // Development reset function
  async function resetData() {
    const service = SubscriptionService.getInstance();
    await service.resetForTesting();
    location.reload();
  }
  
  onMount(() => {
    loadLicenseInfo();
  });
</script>

<div class="subscription-settings">
  <h2 class="section-title">구독 관리</h2>
  <p class="section-description">구독 상태와 사용량을 확인하고 관리합니다</p>
  
  <!-- Current Status -->
  <div class="settings-group">
    <h3 class="group-title">현재 구독 상태</h3>
    <SubscriptionStatusCard />
  </div>
  
  <!-- Usage Statistics -->
  <div class="settings-group">
    <div class="usage-section-header">
      <h3 class="group-title">오늘의 사용량</h3>
      <UsageResetTimer />
    </div>
    <p class="group-description">매일 한국시간 자정에 초기화됩니다</p>
    <UsageStatistics />
  </div>
  
  <!-- Features Comparison -->
  <div class="settings-group">
    <h3 class="group-title">기능 비교</h3>
    <p class="group-description">무료와 프리미엄 플랜의 차이를 확인하세요</p>
    <FeatureComparisonTable />
  </div>
  
  <!-- License Management -->
  <div class="settings-group">
    <h3 class="group-title">라이센스 관리</h3>

    {#if $subscriptionType === 'premium' || $subscriptionType === 'beta'}
      <ActiveLicenseDisplay
        licenseInfo={currentLicenseInfo}
        onDeactivate={handleLicenseDeactivated}
      />
    {:else}
      <LicenseActivationForm onActivated={handleLicenseActivated} />
    {/if}
  </div>
  
  <!-- Subscription Management Links -->
  {#if $subscriptionType === 'premium'}
    <div class="settings-group">
      <h3 class="group-title">구독 관리</h3>
      
      <div class="manage-subscription">
        <p class="manage-description">
          Gumroad에서 구독을 관리하고 결제 정보를 변경할 수 있습니다.
        </p>
        <a 
          href="https://customers.gumroad.com/subscriptions" 
          target="_blank" 
          rel="noopener noreferrer"
          class="manage-link"
        >
          <span>Gumroad 구독 관리</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
            <polyline points="15 3 21 3 21 9"></polyline>
            <line x1="10" y1="14" x2="21" y2="3"></line>
          </svg>
        </a>
      </div>
    </div>
  {/if}
</div>

<style>
  .subscription-settings {
    padding: 24px;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .section-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0 0 24px 0;
  }
  
  .settings-group {
    padding: 24px 0;
    border-bottom: 1px solid var(--border-primary);
  }
  
  .settings-group:last-child {
    border-bottom: none;
  }
  
  .group-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0 0 8px 0;
  }
  
  .group-description {
    font-size: 12px;
    color: var(--text-secondary);
    margin: 0 0 16px 0;
  }
  
  .usage-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }
  
  .usage-section-header .group-title {
    margin: 0;
  }
  
  .manage-subscription {
    padding: 16px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
  }
  
  .manage-description {
    font-size: 14px;
    color: #6b7280;
    margin: 0 0 12px 0;
  }
  
  .manage-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: white;
    color: #6366f1;
    border: 1px solid #e0e7ff;
    border-radius: 8px;
    text-decoration: none;
    font-size: 14px;
    font-weight: 500;
    transition: all 0.2s;
  }
  
  .manage-link:hover {
    background: #f0f4ff;
    border-color: #c7d2fe;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.1);
  }
  
  /* Dark mode support */
  @media (prefers-color-scheme: dark) {
    .manage-subscription {
      background: #111827;
      border-color: #374151;
    }
    
    .manage-description {
      color: #9ca3af;
    }
    
    .manage-link {
      background: #1f2937;
      color: #818cf8;
      border-color: #374151;
    }
    
    .manage-link:hover {
      background: #374151;
      border-color: #4b5563;
      box-shadow: 0 2px 8px rgba(129, 140, 248, 0.1);
    }
  }
  
  :global(.dark-theme) .manage-subscription {
    background: #111827;
    border-color: #374151;
  }
  
  :global(.dark-theme) .manage-description {
    color: #9ca3af;
  }
  
  :global(.dark-theme) .manage-link {
    background: #1f2937;
    color: #818cf8;
    border-color: #374151;
  }
  
  :global(.dark-theme) .manage-link:hover {
    background: #374151;
    border-color: #4b5563;
    box-shadow: 0 2px 8px rgba(129, 140, 248, 0.1);
  }
</style>